import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AuthCallback from './components/AuthCallback';
import HomeEnhanced from './pages/HomeEnhanced';
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Editor from './pages/Editor';
import ContentCreationHub from './pages/ContentCreationHub';
import BaseEditor from './components/editor/BaseEditor';
import ArticleDetail from './pages/ArticleDetail';
import Profile from './pages/Profile';
import BrowseEnhanced from './pages/BrowseEnhanced';
import EditorDashboard from './pages/EditorDashboard';
import Bookmarks from './pages/Bookmarks';
import Authors from './pages/Authors';
import LiteraryHeritage from './pages/LiteraryHeritage';
import CommunityWriters from './pages/CommunityWriters';
import AuthorDetail from './pages/AuthorDetail';
import WriterProfile from './pages/WriterProfileEnhanced';
import Competitions from './pages/Competitions';
import Leaderboard from './pages/Leaderboard';
import Settings from './pages/Settings';
import AdminDashboard from './pages/AdminDashboard';
import CmsCompetitionEntries from './components/cms/CmsCompetitionEntries';
import Collections from './pages/Collections';
import CollectionDetail from './pages/CollectionDetail';
import WorkReader from './pages/WorkReader';
import PublicPage from './pages/PublicPage';
import ContactPage from './pages/ContactPage';
import './App.css';

function MyProfileRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={`/profile/${user.user_id}`} replace />;
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<HomeEnhanced />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/article/:articleId" element={<ArticleDetail />} />
      <Route path="/profile/:userId" element={<Profile />} />
      <Route path="/profile" element={<MyProfileRedirect />} />
      <Route path="/me" element={<MyProfileRedirect />} />
      <Route path="/browse" element={<BrowseEnhanced />} />
      <Route path="/authors" element={<Authors />} />
      <Route path="/authors/heritage" element={<LiteraryHeritage />} />
      <Route path="/authors/community" element={<CommunityWriters />} />
      <Route path="/author/:authorId" element={<AuthorDetail />} />
      <Route path="/writer/:username" element={<WriterProfile />} />
      <Route path="/competitions" element={<Competitions />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/collections" element={<Collections />} />
      <Route path="/collections/:collectionId" element={<CollectionDetail />} />
      <Route path="/work/:workId" element={<WorkReader />} />
      <Route path="/page/:slug" element={<PublicPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      {/* Content Creation Hub - New System */}
      <Route
        path="/editor"
        element={
          <ProtectedRoute>
            <ContentCreationHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/editor/new/:contentType"
        element={
          <ProtectedRoute>
            <BaseEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/editor/edit/:contentId"
        element={
          <ProtectedRoute>
            <BaseEditor />
          </ProtectedRoute>
        }
      />
      {/* Legacy Editor - Keep for backward compatibility */}
      <Route
        path="/editor/:articleId"
        element={
          <ProtectedRoute>
            <Editor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/editor-console"
        element={
          <ProtectedRoute>
            <EditorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookmarks"
        element={
          <ProtectedRoute>
            <Bookmarks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/competitions/:competitionId/entries"
        element={
          <AdminRoute>
            <CmsCompetitionEntries />
          </AdminRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <div className="App">
            <AppRouter />
            <Toaster position="top-center" richColors />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
